import React, { useState, useMemo, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import {
  format,
  addHours,
  addMinutes,
  subMinutes,
  subHours,
  getHours,
  getMinutes,
  setMinutes,
  setHours,
  setSeconds,
  isAfter,
  isSameHour,
  isSameMinute,
} from 'date-fns'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'lodash'

import {
  FormControl,
  TextInputSearchMulti,
  DatePicker,
  Dropdown,
  Loader,
  PriorityLabel,
  TextareaInput,
} from 'components'

import {
  findSelected as findSelectedPriority,
  trimFirstSpace,
} from 'helpers/common'
import { IconClock } from 'components/Icons'
import { getUsers, PRIORITY_FORM_OPTIONS } from 'features/Ticket'
import type { OptionProps } from 'types'
import type { IUsersInfo } from 'features/Ticket'

const dayHours = 24
const dayHoursOptionSize = dayHours * 2

export type PropsTicketAssignForm = {
  users: IUsersInfo[]
  statusPriority: string
}

function TicketAssignForm(props: PropsTicketAssignForm): JSX.Element {
  const { statusPriority } = props

  const [, setUsersChecking] = useState<boolean>(false)
  const [statusPriorityChecking, setStatusPriorityChecking] =
    useState<boolean>(false)
  const [priorities, setPriorities] = useState<OptionProps[]>([])
  const [isErrUsers, setIsErrUsers] = useState(false)

  const { control, setValue, getValues, trigger, formState, watch } =
    useFormContext()
  const { isSubmitted } = formState
  const { t } = useTranslation('ticket')

  const formDiv = document.getElementById('assign-content-form')

  useEffect(() => {
    const _priorities: OptionProps[] = []
    PRIORITY_FORM_OPTIONS.forEach((value, index) => {
      _priorities[index] = {
        ...value,
        icon: <PriorityLabel status={value.key} />,
      }
    })
    setPriorities(_priorities)

    setValue('statusPriority', statusPriority, { shouldDirty: true })
  }, [])

  useEffect(() => {
    if (isSubmitted) handleErrUsers(watch('users'))
  }, [isSubmitted])

  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const base = 'f-ticket-assign-modal'

  const optionTimes: OptionProps<Date>[] = useMemo<OptionProps<Date>[]>(
    () =>
      Array(dayHoursOptionSize)
        .fill(setHours(setMinutes(setSeconds(new Date(), 0), 0), 0))
        .map((date, incrementor) => ({
          key: addMinutes(date, 30 * incrementor),
          label: format(addMinutes(date, 30 * incrementor), 'HH:mm'),
        })),
    [],
  )

  const isDateConflict = (start: Date, end: Date): boolean => {
    const _isAfter = isAfter(start, end)
    const _isSame = isSameHour(start, end) && isSameMinute(start, end)
    return _isAfter || _isSame
  }

  const handleSelectAssignStart = (selected: OptionProps<Date>): void => {
    const edgeTime: Date = setHours(
      setMinutes(setSeconds(new Date(), 0), 0),
      23,
    )
    const nextEnd: Date = isSameHour(selected.key, edgeTime)
      ? addMinutes(selected.key, 30)
      : addHours(selected.key, 1)

    setValue('assignEnd', nextEnd, { shouldDirty: true })
    setValue('assignStart', selected.key, { shouldDirty: true })
  }

  const handleSelectAssignEnd = (selected: OptionProps<Date>): void => {
    const isSameTime =
      isSameHour(selected.key, getValues('assignStart')) &&
      isSameMinute(selected.key, getValues('assignStart'))

    const edgeTime: Date = setHours(
      setMinutes(setSeconds(new Date(), 0), 30),
      0,
    )

    const nextStart: Date = isSameHour(selected.key, edgeTime)
      ? subMinutes(selected.key, 30)
      : subHours(selected.key, 1)

    if (!isDateConflict(getValues('assignStart'), selected.key)) {
      return setValue('assignEnd', selected.key, { shouldDirty: true })
    }

    if (isSameTime) {
      const nextEnd: Date = isSameHour(selected.key, edgeTime)
        ? addMinutes(selected.key, 30)
        : addHours(selected.key, 1)

      return setValue('assignEnd', nextEnd, { shouldDirty: true })
    }

    setValue('assignStart', nextStart, { shouldDirty: true })
    setValue('assignEnd', selected.key, { shouldDirty: true })
  }

  const findSelected = (
    options: OptionProps<Date>[],
    key: Date,
  ): OptionProps<Date> | null => {
    const result =
      options.find((option) => {
        const isEqualHour = getHours(option.key) === getHours(key)
        const isEqualMinute = getMinutes(option.key) === getMinutes(key)

        return isEqualHour && isEqualMinute
      }) || null

    return result
  }

  const handleChangeUsers = (items: OptionProps[]) => {
    handleErrUsers(items)
    setValue('users', items, { shouldDirty: true })
  }

  const handleErrUsers = (items: OptionProps[]) => {
    if (isSubmitted && isEmpty(items)) {
      formDiv?.scrollTo(0, 0)
      formDiv?.animate({ scrollTop: 0 })

      setIsErrUsers(true)
    } else {
      setIsErrUsers(false)
    }
  }

  return (
    <div className={`${base}__content-form`}>
      <FormControl label={t('priority.title')} required>
        <Controller
          type='text'
          name='statusPriority'
          control={control}
          defaultValue={watch('statusPriority')}
          render={(field) => (
            <Dropdown
              className={`${base}__form-dropdown`}
              placeholder={t('selectPriority.placeholder')}
              options={priorities}
              onChange={(value) =>
                setValue('statusPriority', value.key, { shouldDirty: true })
              }
              selected={findSelectedPriority(
                priorities,
                field.value || watch('statusPriority'),
              )}
              isShowIcon
              onBlur={async () => {
                setStatusPriorityChecking(true)
                await trigger('statusPriority')
                setStatusPriorityChecking(false)
              }}
              withIcon={statusPriorityChecking && <Loader />}
              ref={field.ref}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('assignTo.title')} required>
        <Controller
          type='text'
          name='users'
          control={control}
          defaultValue={watch('users')}
          render={() => (
            <TextInputSearchMulti
              placeholder={t('select.placeholder')}
              selected={watch('users')}
              onChange={handleChangeUsers}
              onBlur={async () => {
                setUsersChecking(true)
                await handleErrUsers(watch('users'))
                await trigger('users')
                setUsersChecking(false)
              }}
              loadOptions={getUsers}
              isError={isErrUsers}
              height={130}
              position='baseline'
              fullWidth
            />
          )}
        />
      </FormControl>

      <div className={`${base}__form-remark-layout`}>
        <FormControl label={t('remark.title')}>
          <Controller
            name='issueDetail'
            control={control}
            defaultValue={''}
            maxLength={1000}
            render={(fields) => (
              <TextareaInput
                className={`${base}__form-remark`}
                hasCountable
                {...fields}
                onChange={(e) => {
                  fields.onChange(trimFirstSpace(e))
                }}
                value={fields.value}
                placeholder={t('insertDetail.placeholder')}
              />
            )}
          />
        </FormControl>
      </div>

      <FormControl label={t('date.title')}>
        <Controller
          type='text'
          name='assignDate'
          control={control}
          defaultValue={selectedDate}
          render={({ value }) => (
            <DatePicker
              value={value || selectedDate}
              onClick={() => {
                if (formDiv) {
                  formDiv.scrollTop = formDiv.scrollHeight
                  formDiv.animate({ scrollTop: formDiv.scrollHeight })
                }
              }}
              onChange={(date) => {
                setSelectedDate(date)
                setValue('assignDate', date, { shouldDirty: true })
              }}
              className={`${base}__date-picker`}
              variants='secondary'
              placeholder={t('selectDate.placeholder')}
              format='dd MMM yyyy'
              fullWidth
            />
          )}
        />
      </FormControl>

      <div className={`${base}__form-time`}>
        <FormControl label={t('startDate.title')}>
          <Controller
            type='text'
            name='assignStart'
            control={control}
            defaultValue={optionTimes[0]}
            render={({ value }) => (
              <Dropdown
                className={`${base}__time-dropdown`}
                placement='top'
                options={optionTimes.slice(0, optionTimes.length - 1)}
                selected={findSelected(optionTimes, value)}
                onChange={handleSelectAssignStart}
                withIcon={<IconClock variants='md' />}
                keyTest={'TicketAssignForm-Dropdown-1'}
                isTime
              />
            )}
          />
        </FormControl>

        <FormControl label={t('endDate.title')}>
          <Controller
            type='text'
            name='assignEnd'
            control={control}
            defaultValue={optionTimes[0]}
            render={({ value }) => (
              <Dropdown
                className={`${base}__time-dropdown`}
                placement='top'
                options={optionTimes.slice(1, optionTimes.length)}
                selected={findSelected(optionTimes, value)}
                onChange={handleSelectAssignEnd}
                withIcon={<IconClock variants='md' />}
                keyTest={'TicketAssignForm-Dropdown-2'}
                isTime
              />
            )}
          />
        </FormControl>
      </div>
    </div>
  )
}

export default TicketAssignForm
